import { useEffect, useMemo, useRef, useState } from "react";
import { FaBars, FaChevronDown, FaSearch, FaTimes } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import API from "../../../utils/api";
import { PLACEHOLDER_IMAGE } from "../../../utils/image";
import RenderToast from "../../RenderToast";
import SocialSection from "../../SocialSection";
import TopBar from "../TopBar";
import styles from "./CategoryMegaMenu.module.css";
import classes from "./HeaderOne.module.css";
import styler from "./ServiceMegaMenu.module.css";

function readStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function HeaderOne() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [authToken, setAuthToken] = useState(localStorage.getItem("authToken"));
  const [user, setUser] = useState(readStoredUser());
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  const drawerRef = useRef();
  const searchWrapRef = useRef();

  const normalizedSearch = search.trim().toLowerCase();
  const uploadBase = API.base?.endsWith("/primecloudsystems_api")
    ? API.base
    : `${API.base}/primecloudsystems_api`;

  const getProductThumb = (imageName) =>
    imageName
      ? `${uploadBase}/upload/${imageName}`
      : `${uploadBase}/upload/placeholder-image.jpg`;

  const suggestionResults = useMemo(() => {
    if (normalizedSearch.length < 2) return [];
    return allProducts
      .filter((p) =>
        String(p.sku || "")
          .toLowerCase()
          .includes(normalizedSearch),
      )
      .slice(0, 6);
  }, [allProducts, normalizedSearch]);

  // Fetch categories
  // useEffect(() => {
  //   setCategoriesLoading(true);
  //   fetch(`${API.base}/categories.php`, {
  //     method: "POST",
  //     headers: { "Content-Type": "application/x-www-form-urlencoded" },
  //     body: new URLSearchParams({ action: "fetch" }),
  //   })
  //     .then((res) => res.json())
  //     .then((data) => {
  //       if (Array.isArray(data)) {
  //         const parents = data
  //           .filter((c) => !c.parent_id && c.categoryName !== "Uncategorized")
  //           .slice(0, 8);

  //         const allCats = parents.map((parent) => ({
  //           ...parent,
  //           children: data.filter((c) => c.parent_id === parent.id),
  //         }));
  //         setCategories(allCats);
  //       }
  //     })
  //     .catch(console.error)
  //     .finally(() => setCategoriesLoading(false));
  // }, []);

  // Fetch categories

  useEffect(() => {
    let isMounted = true;
    setCategoriesLoading(true);

    const controller = new AbortController();
    const timeout = setTimeout(() => {
      console.warn("Categories request timed out");
    }, 1000);

    fetch(`${API.base}/categories.php`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ action: "fetch" }),
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        if (!isMounted) return;
        if (Array.isArray(data)) {
          const parents = data
            .filter((c) => !c.parent_id && c.categoryName !== "Uncategorized")
            .slice(0, 8);

          const allCats = parents.map((parent) => ({
            ...parent,
            children: data.filter((c) => c.parent_id === parent.id),
          }));

          setCategories(allCats);
          setCategoriesLoading(false);
        } else {
          console.error("Invalid categories response", data);
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error("Failed to fetch categories", err);
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // Fetch products
  useEffect(() => {
    fetch(`${API.base}/products.php`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ action: "fetch" }),
    })
      .then((res) => res.json())
      .then((data) =>
        setAllProducts(Array.isArray(data) ? data : data?.data || []),
      )
      .catch(console.error);
  }, []);

  useEffect(
    () => setSuggestionsOpen(normalizedSearch.length >= 2),
    [normalizedSearch],
  );

  useEffect(() => {
    if (!suggestionsOpen) return;
    const handleOutside = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target))
        setSuggestionsOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [suggestionsOpen]);

  // Auth storage updates
  useEffect(() => {
    const onStorage = () => {
      setAuthToken(localStorage.getItem("authToken"));
      setUser(readStoredUser());
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("userUpdated", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("userUpdated", onStorage);
    };
  }, []);

  // Drawer outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (
        drawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(e.target)
      ) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [drawerOpen]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = search.trim().toLowerCase();
    if (!q) return RenderToast({ type: "error", message: "Please enter SKU" });

    const product = allProducts.find(
      (p) => p.sku?.toLowerCase() === q || p.productName?.toLowerCase() === q,
    );
    if (product) {
      navigate(`/product/${product.product_slug}`);
      setSearch("");
      setSuggestionsOpen(false);
    } else RenderToast({ type: "error", message: "Product not found" });
  };

  const handleSuggestionSelect = (product) => {
    navigate(`/product/${product.product_slug || product.sku}`);
    setSearch("");
    setSuggestionsOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    window.dispatchEvent(new Event("userUpdated"));
    setAuthToken(null);
    setUser(null);
    navigate("/");
  };

  return (
    <header className={classes.header}>
      {/* Top Bar */}
      <TopBar />
      {/* Top Row: Logo + Search + Auth */}
      <div className={`${classes.rowFull} ${classes.topRow}`}>
        <div className={classes.rowInner}>
          {/* Logo */}
          <div className={classes.logoWrap}>
            <Link to="/" className={classes.logoLink}>
              <img
                src="/primecloudsystems_logo.webp"
                alt="Prime Cloud Systems"
                className={classes.logoImg}
              />
            </Link>
          </div>

          <div className={classes.centerWrap}>
            <Link to="/">Home</Link>
            <Link to="/about-us">About Us</Link>

            {/* Categories Mega Menu */}
            <div className={styles.categoryMegaMenuDropdown}>
              <span
                className={styles.categoryMegaMenuTrigger}
                onClick={() => setCategoriesOpen(!categoriesOpen)}
              >
                Categories{" "}
                <FaChevronDown
                  className={`${styles.downArrow} ${
                    categoriesOpen ? styles.open : ""
                  }`}
                />
              </span>

              <div className={styles.categoryMegaMenuDropdownMenu}>
                <div className={styles.categoryMegaMenuColumn1}>
                  <h3 className={styles.categoryMegaMenuHeading}>
                    Browse Categories
                  </h3>
                  <p className={styles.categoryMegaMenuDescription}>
                    Explore our product categories to find exactly what you
                    need.
                  </p>
                  <Link
                    to="/category"
                    className={styles.categoryMegaMenuButton}
                  >
                    View All Categories
                  </Link>
                </div>

                <div className={styles.categoryMegaMenuColumn2}>
                  {categoriesLoading ? (
                    <>
                      {[0, 1].map((rowIndex) => (
                        <div
                          key={rowIndex}
                          className={styles.categoryMegaMenuRow}
                        >
                          {[0, 1, 2, 3].map((colIndex) => (
                            <div
                              key={colIndex}
                              className={styles.categoryMegaMenuParent}
                            >
                              <div className={classes.skeletonParentLink}></div>
                              <ul className={styles.categoryMegaMenuChildList}>
                                {[0, 1, 2].map((childIndex) => (
                                  <li
                                    key={childIndex}
                                    className={styles.categoryMegaMenuChildItem}
                                  >
                                    <div
                                      className={classes.skeletonChildLink}
                                    ></div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      {[0, 1].map((rowIndex) => (
                        <div
                          key={rowIndex}
                          className={styles.categoryMegaMenuRow}
                        >
                          {categories
                            .slice(rowIndex * 4, rowIndex * 4 + 4)
                            .map((parent) => (
                              <div
                                key={parent.id}
                                className={styles.categoryMegaMenuParent}
                              >
                                <Link
                                  to={`/category/${
                                    parent.category_slug || parent.id
                                  }`}
                                  className={styles.categoryMegaMenuParentLink}
                                >
                                  {parent.categoryName}
                                </Link>
                                {parent.children?.length > 0 && (
                                  <ul
                                    className={styles.categoryMegaMenuChildList}
                                  >
                                    {parent.children.map((child) => (
                                      <li
                                        key={child.id}
                                        className={
                                          styles.categoryMegaMenuChildItem
                                        }
                                      >
                                        <Link
                                          to={`/category/${
                                            child.category_slug || child.id
                                          }`}
                                          className={
                                            styles.categoryMegaMenuChildLink
                                          }
                                        >
                                          {child.categoryName}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ))}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
            <Link to="/shop">Shop </Link>
            <Link to="/contact-us">Contact Us </Link>
          </div>

          {/* Right */}
          <div className={classes.authWrap}>
            <div className={classes.searchWrap} ref={searchWrapRef}>
              <div className={classes.searchInner}>
                <form
                  onSubmit={handleSearchSubmit}
                  className={classes.searchForm}
                >
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search product SKU..."
                    className={classes.searchInput}
                    onFocus={() =>
                      normalizedSearch.length >= 2 && setSuggestionsOpen(true)
                    }
                  />
                  <button type="submit" className={classes.searchBtn}>
                    <FaSearch />
                  </button>
                </form>

                {suggestionsOpen && (
                  <div className={classes.searchDropdown}>
                    {suggestionResults.length === 0 ? (
                      <p className={classes.searchDropdownEmpty}>
                        No matching products
                      </p>
                    ) : (
                      suggestionResults.map((product) => (
                        <button
                          type="button"
                          key={product.id ?? product.sku}
                          className={classes.searchDropdownItem}
                          onClick={() => handleSuggestionSelect(product)}
                        >
                          <img
                            src={getProductThumb(product.productImage)}
                            alt={product.productName}
                            className={classes.searchDropdownImage}
                            onError={(e) =>
                              (e.currentTarget.src = PLACEHOLDER_IMAGE)
                            }
                          />
                          <div className={classes.searchDropdownMeta}>
                            <p className={classes.searchDropdownTitle}>
                              {product.productName || "Untitled Product"}
                            </p>
                            <span className={classes.searchDropdownSku}>
                              SKU: {product.sku || "N/A"}
                            </span>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <button
            className={classes.hamburger}
            onClick={() => setDrawerOpen(!drawerOpen)}
          >
            {drawerOpen ? (
              <FaTimes className={classes.iconTransition} />
            ) : (
              <FaBars className={classes.iconTransition} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      <div
        className={`${classes.drawerOverlay} ${
          drawerOpen ? classes.drawerOpen : ""
        }`}
      >
        <div ref={drawerRef} className={classes.drawer}>
          {/* <button
            className={classes.drawerClose}
            onClick={() => setDrawerOpen(false)}
          >
            <FaTimes />
          </button> */}
          <div className={classes.drawerLogo}>
            <img
              src="/primecloudsystems_logo.webp"
              alt="Prime Cloud Systems Logo"
            />
          </div>
          <nav className={classes.drawerLinks}>
            <Link
              to="/"
              className={classes.drawerLink}
              onClick={() => setDrawerOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={classes.drawerLink}
              onClick={() => setDrawerOpen(false)}
            >
              About
            </Link>
            <Link
              to="/shop"
              className={classes.drawerLink}
              onClick={() => setDrawerOpen(false)}
            >
              Shop
            </Link>
            <Link
              to="/category"
              className={classes.drawerLink}
              onClick={() => setDrawerOpen(false)}
            >
              Categories
            </Link>

            <Link
              to="/contact"
              className={classes.drawerLink}
              onClick={() => setDrawerOpen(false)}
            >
              Contact
            </Link>
          </nav>
          <div className={classes.drawerDivider}></div>
          <div className={classes.drawerBottom}>
            <SocialSection />
          </div>
        </div>
      </div>
    </header>
  );
}
